import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, Calendar, Users, CheckCircle, Trophy,
  Star, TrendingUp, AlertCircle, ChevronLeft, ChevronRight,
  UtensilsCrossed, Bell, Cloud, Sun, CloudRain, CloudSnow, Wind,
  Home, UserCheck, CircleDot, Check, ShieldCheck, ChevronDown, ChevronUp
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { format, isToday, isTomorrow, startOfWeek, endOfWeek } from 'date-fns';
import { useTouch } from '@/hooks/useTouch';
import { useAutoDim } from '../hooks/useAutoDim';
import { APP_VERSION } from '@/version';
import { useNavigate } from 'react-router-dom';
import { CookieUtils, KIOSK_COOKIES } from '@/utils/cookies';
import InlineChoreStatus from '@/components/kiosk/InlineChoreStatus';
import { useConfetti } from '@/components/ConfettiCelebration';

interface KioskConfig {
  family_name?: string;
  family_slug?: string;
  kiosk_type?: string;
  features?: Record<string, boolean>;
  widgets?: Array<{ id: string; enabled: boolean }>;
}

interface Chore {
  id: string;
  chore_name: string;
  assigned_to: string;
  status: 'pending' | 'completed' | 'verified';
  points_offered: number;
  icon?: string;
  due_date: string;
}

interface ChoresSummary {
  chores: Chore[];
  completed_count: number;
  pending_count: number;
  verified_count: number;
}

interface PresenceInfo {
  child: string;
  is_present: boolean;
  is_parent: boolean;
}

interface DefaultKioskDashboardProps {
  config?: KioskConfig;
}

export const DefaultKioskDashboard: React.FC<DefaultKioskDashboardProps> = ({ config }) => {
  // Helper to check if a feature is enabled (defaults to true if no config)
  const isFeatureEnabled = (featureKey: string): boolean => {
    if (!config?.features) return true; // Show all widgets if no config
    return config.features[featureKey] !== false; // Default to enabled
  };
  const navigate = useNavigate();
  const { celebrate } = useConfetti();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [choresSummary, setChoresSummary] = useState<ChoresSummary | null>(null);
  const [tomorrowChores, setTomorrowChores] = useState<Chore[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [announcement, setAnnouncement] = useState<any>(null);
  const [dinner, setDinner] = useState<any>(null);
  const [weekMeals, setWeekMeals] = useState<any[]>([]);
  const [presence, setPresence] = useState<PresenceInfo[]>([]);
  const [weekPoints, setWeekPoints] = useState<Record<string, number>>({});
  const [weather, setWeather] = useState<{
    temperature: number;
    condition: string;
    high: number;
    low: number;
    humidity?: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [childBreakdown, setChildBreakdown] = useState<any>(null);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);

  // Collapsible widget state - load from localStorage
  const [collapsedWidgets, setCollapsedWidgets] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('kiosk_collapsed_widgets');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const toggleWidget = (widgetId: string) => {
    setCollapsedWidgets(prev => {
      const updated = { ...prev, [widgetId]: !prev[widgetId] };
      localStorage.setItem('kiosk_collapsed_widgets', JSON.stringify(updated));
      return updated;
    });
  };

  const isCollapsed = (widgetId: string) => collapsedWidgets[widgetId] || false;

  // Check for kiosk device token on mount - redirect to pairing if missing
  // Check both localStorage AND cookies for maximum persistence
  useEffect(() => {
    const localToken = localStorage.getItem('kiosk_device_token');
    const cookieToken = CookieUtils.getCookie(KIOSK_COOKIES.DEVICE_TOKEN);

    // If we have a cookie but not localStorage, restore from cookie
    if (cookieToken && !localToken) {
      console.log('[Kiosk] Restoring device token from cookie to localStorage');
      localStorage.setItem('kiosk_device_token', cookieToken);

      // Restore other fields from cookies too
      const deviceId = CookieUtils.getCookie(KIOSK_COOKIES.DEVICE_ID);
      const deviceName = CookieUtils.getCookie(KIOSK_COOKIES.DEVICE_NAME);
      const pairedAt = CookieUtils.getCookie(KIOSK_COOKIES.PAIRED_AT);

      if (deviceId) localStorage.setItem('kiosk_device_id', deviceId);
      if (deviceName) localStorage.setItem('kiosk_device_name', deviceName);
      if (pairedAt) localStorage.setItem('kiosk_paired_at', pairedAt);
    }

    // If we have neither localStorage nor cookie, redirect to activate page
    // This will auto-generate a pairing code for the parent to approve
    if (!localToken && !cookieToken) {
      console.log('[Kiosk] No device token found in localStorage or cookies, redirecting to activate page');
      navigate('/kiosk/activate');
    }
  }, [navigate]);

  // Enable touch support for kiosk
  useTouch();

  // Auto-dim functionality
  const { brightness, isDimmed, wakeUp } = useAutoDim({
    dimStartHour: 21,
    dimEndHour: 7,
    dimOpacity: 0.3,
    idleTimeout: 600000
  });

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get weather icon based on condition
  const getWeatherIcon = (condition: string) => {
    const lowerCondition = condition.toLowerCase();
    if (lowerCondition.includes('rain') || lowerCondition.includes('shower')) {
      return <CloudRain className="w-10 h-10 text-blue-300" />;
    } else if (lowerCondition.includes('snow') || lowerCondition.includes('sleet')) {
      return <CloudSnow className="w-10 h-10 text-blue-100" />;
    } else if (lowerCondition.includes('clear') || lowerCondition.includes('sunny')) {
      return <Sun className="w-10 h-10 text-yellow-300" />;
    } else if (lowerCondition.includes('cloud') || lowerCondition.includes('overcast')) {
      return <Cloud className="w-10 h-10 text-gray-300" />;
    } else if (lowerCondition.includes('wind')) {
      return <Wind className="w-10 h-10 text-gray-300" />;
    }
    return <Sun className="w-10 h-10 text-yellow-300" />;
  };

  // Fetch weather
  const fetchWeather = async () => {
    try {
      const response = await apiClient.getKioskWeather();
      if (response) {
        setWeather({
          temperature: response.temperature || 75,
          condition: response.condition || 'Clear',
          high: response.high || 82,
          low: response.low || 65,
          humidity: response.humidity
        });
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
    }
  };

  // Fetch leaderboard
  const fetchLeaderboard = async () => {
    try {
      const response = await apiClient.get('/api/kiosk/leaderboard/weekly');
      setLeaderboard(response);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  // Fetch child breakdown when clicking on leaderboard
  const fetchChildBreakdown = async (username: string) => {
    try {
      const response = await apiClient.get(`/api/points/breakdown/${username}?period=week`);
      setChildBreakdown(response);
      setSelectedChild(username);
      setShowBreakdownModal(true);
    } catch (error) {
      console.error('Error fetching child breakdown:', error);
    }
  };

  // Load kiosk data
  const loadKioskData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      // Load all data in parallel
      const [statusRes, calendarRes] = await Promise.allSettled([
        apiClient.getKioskStatus(),
        apiClient.get('/api/kiosk/external-calendar-events', {
          params: {
            start_date: format(startOfWeek(new Date()), 'yyyy-MM-dd'),
            end_date: format(endOfWeek(new Date()), 'yyyy-MM-dd')
          }
        })
      ]);

      // Process status data
      if (statusRes.status === 'fulfilled') {
        const status = statusRes.value;
        setChoresSummary(status.chores_summary || null);
        setTomorrowChores(status.tomorrow_chores || []);
        setAnnouncement(status.announcement || null);
        setDinner(status.dinner || null);
        setWeekMeals(status.week_meals || []);
        setPresence(status.presence || []);
        setWeekPoints(status.week_points || {});
      }

      // Process calendar data
      if (calendarRes.status === 'fulfilled') {
        setCalendarEvents(calendarRes.value || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading kiosk data:', error);
      setError('Failed to load kiosk data');
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadKioskData(true);
    fetchLeaderboard();
    fetchWeather();
  }, []);

  // Refresh data every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      loadKioskData(false);
      fetchLeaderboard();
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  // Refresh weather every 30 minutes
  useEffect(() => {
    const weatherInterval = setInterval(() => {
      fetchWeather();
    }, 1800000); // 30 minutes
    return () => clearInterval(weatherInterval);
  }, []);

  // Group chores by child for better visualization
  const choresByChild = React.useMemo(() => {
    if (!choresSummary?.chores) return {};
    return choresSummary.chores.reduce((acc, chore) => {
      const child = chore.assigned_to;
      if (!acc[child]) acc[child] = [];
      acc[child].push(chore);
      return acc;
    }, {} as Record<string, Chore[]>);
  }, [choresSummary]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading kiosk...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-2xl">Error: {error}</div>
      </div>
    );
  }

  const totalChores = choresSummary ? choresSummary.pending_count + choresSummary.completed_count + choresSummary.verified_count : 0;
  const progressPercent = totalChores > 0 ? ((choresSummary?.completed_count || 0) + (choresSummary?.verified_count || 0)) / totalChores * 100 : 0;

  return (
    <div
      className="h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 p-6 flex flex-col overflow-hidden"
      style={{ opacity: brightness }}
      onClick={wakeUp}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-4 flex-shrink-0">
        <div className="text-white">
          <div className="text-5xl font-bold">
            {format(currentTime, 'h:mm')}
            <span className="text-3xl opacity-70">{format(currentTime, 'a')}</span>
          </div>
          <div className="text-2xl opacity-80 mt-1">
            {format(currentTime, 'EEEE, MMMM d')}
          </div>
        </div>

        {/* Weather Widget */}
        {isFeatureEnabled('kiosk_weather') && weather && (
          <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
            {getWeatherIcon(weather.condition)}
            <div className="text-white">
              <div className="text-3xl font-bold">{weather.temperature}°</div>
              <div className="text-sm opacity-80">{weather.condition}</div>
            </div>
            <div className="text-white/70 text-sm border-l border-white/20 pl-4">
              <div>H: {weather.high}°</div>
              <div>L: {weather.low}°</div>
            </div>
          </div>
        )}

        <div className="text-right text-white">
          <div className="text-base font-semibold text-gray-200">
            HousePoints
          </div>
          <div className="text-xs text-gray-400 mt-1">
            v{APP_VERSION}
          </div>
        </div>
      </div>

      {/* Announcement Banner */}
      {isFeatureEnabled('kiosk_announcements') && announcement && (
        <div className="bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/50 rounded-xl p-4 mb-4 flex-shrink-0">
          <div className="flex items-start">
            <Bell className="w-6 h-6 text-yellow-300 mr-3 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-yellow-200 mb-1">
                {announcement.title}
              </h3>
              <p className="text-yellow-100">{announcement.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6 flex-1 min-h-0">
        {/* Left Column - Chores Progress & Calendar */}
        <div className="col-span-8 flex flex-col gap-4 min-h-0">
          {/* Chores Progress Card */}
          {isFeatureEnabled('kiosk_chores') && choresSummary && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 flex-1 flex flex-col min-h-0">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <CheckCircle className="w-7 h-7 mr-2 text-green-400" />
                  Today's Chores
                </h2>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center text-gray-400">
                    <CircleDot className="w-4 h-4 mr-1" />
                    {choresSummary.pending_count} pending
                  </span>
                  <span className="flex items-center text-green-400">
                    <Check className="w-4 h-4 mr-1" />
                    {choresSummary.completed_count} done
                  </span>
                  <span className="flex items-center text-blue-400">
                    <ShieldCheck className="w-4 h-4 mr-1" />
                    {choresSummary.verified_count} verified
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4 flex-shrink-0">
                <div className="flex justify-between text-sm text-white/70 mb-2">
                  <span>Daily Progress</span>
                  <span>{Math.round(progressPercent)}% complete</span>
                </div>
                <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full flex">
                    {/* Verified portion (blue) */}
                    <div
                      className="bg-blue-500 transition-all duration-500"
                      style={{ width: `${totalChores > 0 ? (choresSummary.verified_count / totalChores) * 100 : 0}%` }}
                    />
                    {/* Completed portion (green) */}
                    <div
                      className="bg-green-500 transition-all duration-500"
                      style={{ width: `${totalChores > 0 ? (choresSummary.completed_count / totalChores) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center"><span className="w-2 h-2 bg-blue-500 rounded-full mr-1"></span>Verified</span>
                    <span className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>Completed</span>
                    <span className="flex items-center"><span className="w-2 h-2 bg-white/20 rounded-full mr-1"></span>Pending</span>
                  </div>
                </div>
              </div>

              {/* Chores by Child */}
              {Object.keys(choresByChild).length === 0 ? (
                <p className="text-white/70 text-lg">No chores assigned for today</p>
              ) : (
                <div className="space-y-4 flex-1 overflow-y-auto min-h-0">
                  {Object.entries(choresByChild).map(([child, chores]) => {
                    const childDone = chores.filter(c => c.status !== 'pending').length;
                    const childTotal = chores.length;
                    const childProgress = (childDone / childTotal) * 100;

                    return (
                      <div key={child} className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold mr-3">
                              {child.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-white">{child}</div>
                              <div className="text-white/60 text-sm">
                                {childDone}/{childTotal} complete • {weekPoints[child] || 0} pts this week
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-white">{Math.round(childProgress)}%</div>
                          </div>
                        </div>

                        {/* Child's progress bar */}
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                          <div
                            className={`h-full transition-all duration-500 ${childProgress === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${childProgress}%` }}
                          />
                        </div>

                        {/* Child's chores list */}
                        <div className="grid grid-cols-2 gap-2">
                          {chores.map((chore) => (
                            <div
                              key={chore.id}
                              className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                                chore.status === 'verified'
                                  ? 'bg-blue-500/20 text-blue-200'
                                  : chore.status === 'completed'
                                  ? 'bg-green-500/20 text-green-200'
                                  : 'bg-white/5 text-white/80'
                              }`}
                            >
                              <span className="flex items-center truncate flex-1 min-w-0">
                                {chore.status === 'verified' ? (
                                  <ShieldCheck className="w-4 h-4 mr-2 flex-shrink-0" />
                                ) : chore.status === 'completed' ? (
                                  <Check className="w-4 h-4 mr-2 flex-shrink-0" />
                                ) : (
                                  <CircleDot className="w-4 h-4 mr-2 flex-shrink-0 opacity-50" />
                                )}
                                <span className="truncate">{chore.chore_name}</span>
                              </span>
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                {chore.due_date && !isNaN(new Date(chore.due_date + 'T12:00:00').getTime()) && (
                                  <span className="text-xs opacity-50">{format(new Date(chore.due_date + 'T12:00:00'), 'EEE')}</span>
                                )}
                                <span className="text-xs opacity-70">{chore.points_offered}pts</span>
                                <InlineChoreStatus
                                  status={chore.status === 'pending' ? 'assigned' : chore.status}
                                  onStatusChange={async (newStatus: string) => {
                                    try {
                                      if (newStatus === 'completed') {
                                        await apiClient.post(`/api/kiosk/assignments/${chore.id}/complete`);
                                        celebrate();
                                      }
                                      loadKioskData();
                                    } catch (error) {
                                      console.error('Failed to update chore status:', error);
                                      throw error;
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Calendar Events */}
          {isFeatureEnabled('kiosk_calendar') && calendarEvents.length > 0 && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <Calendar className="w-7 h-7 mr-2 text-blue-400" />
                This Week's Events
              </h2>

              <div className="grid grid-cols-2 gap-3">
                {calendarEvents.slice(0, 6).map((event, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div className="font-semibold text-white truncate">
                      {event.summary}
                    </div>
                    <div className="text-white/70 text-sm mt-1">
                      {!isNaN(new Date(event.start).getTime()) ? format(new Date(event.start), 'EEE, MMM d') : 'TBD'}
                      {event.start_time && ` at ${event.start_time}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Dinner, Presence, Leaderboard */}
        <div className="col-span-4 flex flex-col gap-4 min-h-0 overflow-y-auto">
          {/* Tonight's Dinner & Meal Plan */}
          {isFeatureEnabled('kiosk_meals') && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex-shrink-0">
              <button
                onClick={() => toggleWidget('dinner')}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 rounded-t-2xl transition-colors"
              >
                <h2 className="text-xl font-bold text-white flex items-center">
                  <UtensilsCrossed className="w-6 h-6 mr-2 text-orange-400" />
                  Dinner
                  {isCollapsed('dinner') && dinner && (
                    <span className="ml-3 text-base font-normal text-white/60">
                      {dinner.emoji} {dinner.meal_name}
                    </span>
                  )}
                </h2>
                {isCollapsed('dinner') ? (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                )}
              </button>

              {!isCollapsed('dinner') && (
                <div className="px-4 pb-4">
                  {dinner && (
                    <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-4 border border-orange-400/30 mb-4">
                      <div className="text-sm text-orange-200 mb-1">Tonight</div>
                      <div className="text-2xl text-white font-bold flex items-center">
                        {dinner.emoji && <span className="mr-2">{dinner.emoji}</span>}
                        {dinner.meal_name}
                      </div>
                      {dinner.cook?.name && (
                        <div className="text-orange-200 text-sm mt-1">
                          Cook: {dinner.cook.name}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Week's Meals */}
                  {weekMeals.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm text-white/60 mb-2">This Week</div>
                      {weekMeals.slice(0, 4).map((meal, idx) => {
                        const mealDate = new Date(meal.date + 'T12:00:00');
                        const isTonight = isToday(mealDate);
                        return (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-2 rounded-lg ${
                              isTonight ? 'bg-orange-500/20 border border-orange-400/30' : 'bg-white/5'
                            }`}
                          >
                            <span className="text-white/70 text-sm w-16">
                              {format(mealDate, 'EEE')}
                            </span>
                            <span className="text-white flex-1 text-sm flex items-center">
                              {meal.emoji && <span className="mr-2">{meal.emoji}</span>}
                              {meal.meal_name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Family Presence */}
          {presence.length > 0 && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex-shrink-0">
              <button
                onClick={() => toggleWidget('presence')}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 rounded-t-2xl transition-colors"
              >
                <h2 className="text-lg font-bold text-white flex items-center">
                  <Home className="w-5 h-5 mr-2 text-purple-400" />
                  Who's Home
                  {isCollapsed('presence') && (
                    <span className="ml-2 text-sm font-normal text-white/60">
                      {presence.filter(p => p.is_present).length}/{presence.length} home
                    </span>
                  )}
                </h2>
                {isCollapsed('presence') ? (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                )}
              </button>

              {!isCollapsed('presence') && (
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-3 gap-2">
                    {presence.map((person, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-xl text-center ${
                          person.is_present
                            ? 'bg-green-500/20 border border-green-400/30'
                            : 'bg-white/5 border border-white/10 opacity-50'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold">
                          {person.child.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-white text-xs truncate">
                          {person.child.split(' ')[0]}
                        </div>
                        <div className="text-xs mt-1">
                          {person.is_present ? (
                            <span className="text-green-400">Home</span>
                          ) : (
                            <span className="text-white/40">Away</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Points Leaderboard */}
          {isFeatureEnabled('kiosk_leaderboard') && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex-shrink-0">
              <button
                onClick={() => toggleWidget('leaderboard')}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 rounded-t-2xl transition-colors"
              >
                <h2 className="text-lg font-bold text-white flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
                  Leaderboard
                  {isCollapsed('leaderboard') && leaderboard.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-yellow-400">
                      #1 {leaderboard[0]?.display_name || leaderboard[0]?.username}: {leaderboard[0]?.total_points || 0}
                    </span>
                  )}
                </h2>
                {isCollapsed('leaderboard') ? (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                )}
              </button>

              {!isCollapsed('leaderboard') && (
                <div className="px-4 pb-4">
                  {leaderboard.length === 0 ? (
                    <p className="text-white/70">No points earned yet this week</p>
                  ) : (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 5).map((child, idx) => (
                        <div
                          key={idx}
                          className="p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-all"
                          onClick={() => fetchChildBreakdown(child.username)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`text-2xl font-bold w-8 ${
                                idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-600' : 'text-white/50'
                              }`}>
                                #{idx + 1}
                              </div>
                              <div className="font-semibold text-white">
                                {child.display_name || child.username}
                              </div>
                            </div>
                            <div className="text-xl font-bold text-yellow-400">
                              {child.total_points || 0}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Child Breakdown Modal */}
      {showBreakdownModal && childBreakdown && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6"
          onClick={() => setShowBreakdownModal(false)}
        >
          <div
            className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-2xl p-8 max-w-2xl w-full border-2 border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-white">
                {childBreakdown.display_name || selectedChild}'s Week
              </h2>
              <button
                onClick={() => setShowBreakdownModal(false)}
                className="text-white/70 hover:text-white text-3xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-4xl font-bold text-yellow-400">
                  {childBreakdown.total_points || 0}
                </div>
                <div className="text-white/70 text-sm mt-1">Total Points</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-4xl font-bold text-green-400">
                  {childBreakdown.tasks_completed || 0}
                </div>
                <div className="text-white/70 text-sm mt-1">Tasks Done</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-4xl font-bold text-blue-400">
                  {childBreakdown.points_redeemed || 0}
                </div>
                <div className="text-white/70 text-sm mt-1">Points Used</div>
              </div>
            </div>

            {childBreakdown.recent_tasks && childBreakdown.recent_tasks.length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Recent Tasks</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {childBreakdown.recent_tasks.map((task: any, idx: number) => (
                    <div
                      key={idx}
                      className="bg-white/5 rounded-lg p-3 flex justify-between items-center"
                    >
                      <div>
                        <div className="text-white font-medium">{task.title}</div>
                        <div className="text-white/60 text-sm">
                          {!isNaN(new Date(task.completed_at).getTime()) ? format(new Date(task.completed_at), 'MMM d, h:mm a') : ''}
                        </div>
                      </div>
                      <div className="text-yellow-400 font-bold">
                        +{task.points}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DefaultKioskDashboard;
